import React, { useState } from 'react';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import CtaBand from '@/components/CtaBand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function FinancingPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', project_amount: '', notes: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/financing', { ...form, project_amount: form.project_amount ? Number(form.project_amount) : undefined });
      setDone(true);
      toast.success('Pre-qualification submitted!');
    } catch { toast.error('Something went wrong'); } finally { setSending(false); }
  };
  return (
    <Layout>
      <PageHeader eyebrow="Financing" title="Premium projects, flexible payments." subtitle="We partner with leading home improvement lenders to offer 0% intro APR financing on qualifying projects. Pre-qualify in under 60 seconds." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-3">
          {[
            { t: '0% Intro APR', d: 'Available on qualifying projects, up to 18 months.' },
            { t: 'Soft credit check', d: 'Pre-qualifying does not affect your credit score.' },
            { t: 'Trusted partners', d: 'GreenSky, Synchrony, and more.' },
            { t: 'Project amounts', d: 'From $1,000 up to $100,000.' },
          ].map(f => (
            <div key={f.t} className="rounded-2xl border border-[#E6E9EE] bg-white p-4 flex gap-3">
              <Check className="h-5 w-5 text-[#17352D] flex-none mt-0.5" />
              <div><div className="font-semibold text-[#17352D]">{f.t}</div><div className="text-sm text-slate-600">{f.d}</div></div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2">
          {done ? (
            <div className="rounded-2xl border border-[#E6E9EE] bg-[#F2F7F5] p-8 text-center">
              <Check className="h-10 w-10 text-[#17352D] mx-auto" />
              <h2 className="mt-3 font-headline text-2xl text-[#17352D]">Thanks!</h2>
              <p className="text-slate-700 mt-2">A financing specialist will reach out shortly with options for your project.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 space-y-4" data-testid="financing-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label htmlFor="fn">Name *</Label><Input id="fn" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="financing-name-input" /></div>
                <div><Label htmlFor="fe">Email *</Label><Input id="fe" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="financing-email-input" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label htmlFor="fp">Phone</Label><Input id="fp" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="financing-phone-input" /></div>
                <div><Label htmlFor="fa">Project amount (USD)</Label><Input id="fa" type="number" value={form.project_amount} onChange={e => setForm({ ...form, project_amount: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="financing-amount-input" /></div>
              </div>
              <div><Label htmlFor="fnotes">Project details</Label><Textarea id="fnotes" rows={4} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" data-testid="financing-notes-input" /></div>
              <Button type="submit" disabled={sending} data-testid="financing-submit-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6">{sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : 'Pre-Qualify'}</Button>
            </form>
          )}
        </div>
      </section>
      <CtaBand />
    </Layout>
  );
}
